import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import { fetchMyOrganizations } from '@/integrations/api/tenants_api';
import { setActiveTenantId } from '@/integrations/api/token_storage';
import { clearPermissionCache } from '@/hooks/useCan';
import { useAuth } from '@/contexts/AuthContext';
import { Sentry, isSentryEnabled } from '@/lib/sentry';
import { setOrganizationGroup } from '@/lib/posthog';

export type OrgRole = 'owner' | 'admin' | 'supervisor' | 'operator';

export type Org = {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
  plan: string;
};

export type TenantContextValue = {
  currentOrg: Org | null;
  organizations: Org[];
  switchOrg: (orgId: string) => Promise<void>;
  reloadOrganizations: () => Promise<void>;
  role: OrgRole | null;
  isLoading: boolean;
};

// Dev-only fallback orgs. Enabled exclusively when VITE_ENABLE_MOCK_TENANTS === 'true'
// AND running in DEV. Never used in production builds.
const MOCK_ORGS: Org[] =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCK_TENANTS === 'true'
    ? [
        { id: 'org-acme', name: 'Acme Manufacturing (mock)', slug: 'acme', role: 'admin', plan: 'enterprise' },
        { id: 'org-beta', name: 'Beta Industries (mock)', slug: 'beta', role: 'supervisor', plan: 'pro' },
        { id: 'org-gamma', name: 'Gamma Aerospace (mock)', slug: 'gamma', role: 'operator', plan: 'standard' },
      ]
    : [];

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

const storageKey = (userId: string) => `expai_current_org_${userId}`;

type MembershipRow = {
  role: OrgRole;
  tenants: {
    id: string;
    name: string;
    slug: string;
    plan: string | null;
  } | null;
};

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [organizations, setOrganizations] = useState<Org[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Org | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadOrganizations = useCallback(async (): Promise<Org[]> => {
    if (!user) return [];

    let orgs: Org[] = [];

    try {
      if (isExpaiApiEnabled()) {
        const list = await fetchMyOrganizations();
        orgs = list.map((r) => ({
          id: r.id,
          name: r.name,
          slug: r.slug,
          role: r.role,
          plan: r.plan ?? 'standard',
        }));
      } else {
        const { data, error } = await supabase
          .from('tenant_users')
          .select('role, tenants(id, name, slug, plan)')
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (error) {
          console.error('[TenantContext] Failed to load memberships:', error.message);
        } else if (Array.isArray(data)) {
          const rows = data as unknown as MembershipRow[];
          orgs = rows
            .filter((r): r is MembershipRow & { tenants: NonNullable<MembershipRow['tenants']> } => !!r.tenants)
            .map((r) => ({
              id: r.tenants.id,
              name: r.tenants.name,
              slug: r.tenants.slug,
              role: r.role,
              plan: r.tenants.plan ?? 'standard',
            }));
        }
      }
    } catch (e) {
      console.error('[TenantContext] Unexpected error loading memberships:', e);
    }

    if (orgs.length === 0 && MOCK_ORGS.length > 0) {
      console.warn('[TenantContext] No real tenants — using VITE_ENABLE_MOCK_TENANTS fallback.');
      orgs = MOCK_ORGS;
    }

    return orgs;
  }, [user]);

  const reloadOrganizations = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrg(null);
      return;
    }
    setIsLoading(true);
    const orgs = await loadOrganizations();
    setOrganizations(orgs);
    setCurrentOrg((prev) => {
      if (prev && orgs.some((o) => o.id === prev.id)) {
        return orgs.find((o) => o.id === prev.id) ?? orgs[0] ?? null;
      }
      const savedId = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey(user.id)) : null;
      return orgs.find((o) => o.id === savedId) ?? orgs[0] ?? null;
    });
    setIsLoading(false);
  }, [user, loadOrganizations]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user) {
        setOrganizations([]);
        setCurrentOrg(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const orgs = await loadOrganizations();
      if (cancelled) return;

      const savedId = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey(user.id)) : null;
      const initial = orgs.find((o) => o.id === savedId) ?? orgs[0] ?? null;

      setOrganizations(orgs);
      setCurrentOrg(initial);
      setIsLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [user, loadOrganizations]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      const next = organizations.find((o) => o.id === orgId);
      if (!next || !user) return;

      // Re-fetch the membership row so the role reflects the latest
      // server-side value (it may have changed since initial load).
      let freshRole: OrgRole = next.role;
      try {
        if (isExpaiApiEnabled()) {
          const list = await fetchMyOrganizations();
          const row = list.find((o) => o.id === orgId);
          if (row?.role) freshRole = row.role;
        } else {
          const { data, error } = await supabase
            .from('tenant_users')
            .select('role')
            .eq('user_id', user.id)
            .eq('tenant_id', orgId)
            .eq('is_active', true)
            .maybeSingle();
          if (!error && data && (data as { role?: OrgRole }).role) {
            freshRole = (data as { role: OrgRole }).role;
          }
        }
      } catch {
        // ignore — keep cached role
      }

      const refreshed: Org = { ...next, role: freshRole };
      setOrganizations((prev) => prev.map((o) => (o.id === orgId ? refreshed : o)));
      setCurrentOrg(refreshed);

      try {
        window.localStorage.setItem(storageKey(user.id), orgId);
      } catch {
        // ignore
      }
      clearPermissionCache();
      try {
        await queryClient.invalidateQueries();
      } catch {
        // ignore if not available
      }
    },
    [organizations, user, queryClient],
  );

  // Keep the active tenant id in storage so apiRequest can attach X-Tenant-Id
  // by default on every tenant-scoped call (even when callers omit tenantId).
  useEffect(() => {
    setActiveTenantId(currentOrg?.id ?? null);
  }, [currentOrg?.id]);

  // UX-D03 / UX-D04 — tag observability tools with current org.
  useEffect(() => {
    if (isSentryEnabled()) {
      if (currentOrg) {
        Sentry.setTag('org_id', currentOrg.id);
        Sentry.setTag('org_slug', currentOrg.slug);
        Sentry.setTag('user_role', currentOrg.role);
        Sentry.setUser({ id: user?.id ?? 'anon' });
      } else {
        Sentry.setTag('org_id', '');
        Sentry.setTag('org_slug', '');
        Sentry.setTag('user_role', '');
      }
    }
    setOrganizationGroup(currentOrg ? { id: currentOrg.id, name: currentOrg.name, plan: currentOrg.plan } : null);
  }, [currentOrg, user?.id]);

  const value = useMemo<TenantContextValue>(
    () => ({
      currentOrg,
      organizations,
      switchOrg,
      reloadOrganizations,
      role: currentOrg?.role ?? null,
      isLoading,
    }),
    [currentOrg, organizations, switchOrg, reloadOrganizations, isLoading],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
